import { ArrowRight, Play, Sparkles, Zap, Cpu, ShieldCheck, Rocket, Globe2 } from 'lucide-react';
import { Button } from './ui/button';
import Starfield from './Starfield';
import BrandLogo from './BrandLogo';
import { GoogleIcon } from './BrandIcons';

/**
 * OPTION C — "Split Cinematic Bento"
 * Two-column hero: bold copy on the left, a floating browser+device mockup on
 * the right. Below the hero, a bento grid of six feature cards mixing real
 * cinematic imagery with pure gradient tiles. Framer / Linear / Vercel vibe.
 */

const BENTO = [
  {
    span: 'md:col-span-2 md:row-span-2',
    icon: Cpu,
    label: 'AI Orchestration',
    title: 'Multi-model AI, one prompt.',
    body: 'Claude 4.5 · GPT-5 · Gemini 2.5. We route each task to the best brain, automatically.',
    image:
      'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1600&q=85&auto=format&fit=crop',
    accent: 'from-cyan-400 via-indigo-500 to-fuchsia-500',
  },
  {
    icon: Rocket,
    label: 'Global Deploy',
    title: '90-second launch.',
    body: 'SSL, custom domain, CDN &mdash; live in one command.',
    accent: 'from-amber-300 to-rose-500',
  },
  {
    icon: ShieldCheck,
    label: 'Secure',
    title: 'SOC 2 aligned.',
    body: 'Enterprise encryption, secrets vault, private networks.',
    image:
      'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1200&q=85&auto=format&fit=crop',
    accent: 'from-rose-500 via-pink-500 to-red-500',
  },
  {
    icon: Globe2,
    label: 'Global',
    title: '48 regions.',
    body: 'Users on 6 continents get sub-100 ms responses.',
    accent: 'from-emerald-400 to-teal-500',
  },
  {
    icon: Sparkles,
    label: 'Image Gen',
    title: 'Nano Banana built-in.',
    body: 'Photoreal images generated inside your prompts.',
    accent: 'from-fuchsia-500 to-violet-600',
  },
];

export default function HeroSplitCinematic({ onSignIn }) {
  const startGoogleAuth = () => {
    const redirectUrl = window.location.origin + '/build';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section className="relative pt-32 pb-24 text-white overflow-hidden bg-[#030712]">
      <div className="absolute inset-0">
        <Starfield density={130} />
      </div>
      <div className="absolute inset-0 hero-grid opacity-25 pointer-events-none" />
      <div className="aurora-glow aurora-a" />
      <div className="aurora-glow aurora-b" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Split hero */}
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          {/* LEFT */}
          <div>
            <div className="mb-6">
              <BrandLogo size={30} tone="light" />
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 backdrop-blur mb-7">
              <Zap size={12} className="text-cyan-300" />
              <span className="text-[11px] font-bold tracking-widest uppercase text-white/90">
                v3 &middot; New models live
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-[-0.035em] leading-[0.98]">
              <span className="block">Ship real</span>
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-indigo-300 to-fuchsia-300">
                software.
              </span>
              <span className="block">Not just prompts.</span>
            </h1>
            <p className="mt-6 text-slate-300 text-lg max-w-lg leading-relaxed">
              Around the World AI builds, tests, deploys and monetises full-stack
              apps for you &mdash; while you sketch on a napkin.
            </p>

            <div className="mt-8 flex items-center gap-3">
              <Button
                data-testid="split-cta"
                onClick={startGoogleAuth}
                className="h-13 rounded-xl bg-gradient-to-r from-cyan-400 via-indigo-500 to-fuchsia-500 text-white font-bold text-[15px] px-6 shadow-[0_20px_50px_-15px_rgba(99,102,241,0.7)]"
              >
                <GoogleIcon size={18} className="mr-2" />
                Start free &nbsp; <ArrowRight size={16} />
              </Button>
              <Button
                data-testid="split-watch"
                onClick={onSignIn}
                className="h-13 rounded-xl bg-white/5 hover:bg-white/10 border border-white/20 text-white font-semibold px-5"
              >
                <Play size={13} fill="white" className="mr-2 ml-0.5" />
                Live demo
              </Button>
            </div>

            {/* Micro metrics */}
            <div className="mt-10 grid grid-cols-3 gap-5 max-w-md">
              {[
                { v: '4 min', l: 'Avg build' },
                { v: '10k+', l: 'Teams' },
                { v: '99.99%', l: 'Uptime' },
              ].map((m) => (
                <div key={m.l}>
                  <div className="text-2xl font-black bg-clip-text text-transparent bg-gradient-to-br from-white to-slate-400">
                    {m.v}
                  </div>
                  <div className="text-[10px] tracking-widest uppercase text-white/50 font-bold">
                    {m.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — floating device mockup */}
          <div className="relative">
            <div
              className="relative rounded-[28px] border border-white/12 aspect-[4/3] overflow-hidden float-slow"
              style={{
                background:
                  'linear-gradient(180deg, #0b1220 0%, #030817 100%)',
                boxShadow:
                  '0 60px 130px -30px rgba(99,102,241,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
              }}
            >
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/8">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-500/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
                <div className="mx-auto text-[10px] text-white/50 font-mono">
                  aroundtheworld.ai / build
                </div>
              </div>
              {/* Content */}
              <div className="p-5 grid grid-cols-2 gap-3 h-[calc(100%-38px)]">
                <div className="rounded-xl bg-gradient-to-br from-cyan-500/25 to-indigo-600/25 border border-white/8 p-4 flex flex-col">
                  <div className="text-[10px] tracking-widest uppercase text-cyan-200 font-bold">
                    Revenue
                  </div>
                  <div className="text-3xl font-black text-white mt-1">$42.1k</div>
                  <div className="mt-auto flex items-end gap-0.5 h-14">
                    {[8, 14, 10, 20, 16, 24, 30, 28, 36, 32, 40].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t bg-gradient-to-t from-cyan-400/60 to-indigo-400/90"
                        style={{ height: `${h * 2}px` }}
                      />
                    ))}
                  </div>
                </div>
                <div className="rounded-xl bg-white/4 border border-white/8 p-4 flex flex-col">
                  <div className="text-[10px] tracking-widest uppercase text-fuchsia-200 font-bold">
                    Signups
                  </div>
                  <div className="text-3xl font-black text-white mt-1">2,481</div>
                  <div className="text-xs text-emerald-300 mt-1">▲ 24%</div>
                  <div
                    className="mt-auto h-14 rounded-md"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(217,70,239,0.35), rgba(56,189,248,0.35))',
                    }}
                  />
                </div>
                <div className="col-span-2 rounded-xl bg-white/4 border border-white/8 p-4">
                  <div className="text-[10px] tracking-widest uppercase text-emerald-200 font-bold">
                    Active builds
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {['Marketplace', 'CRM', 'Blog'].map((n, i) => (
                      <div key={n} className="flex items-center gap-2 text-xs text-white/85">
                        <span className={`h-1.5 w-1.5 rounded-full ${['bg-cyan-400', 'bg-fuchsia-400', 'bg-emerald-400'][i]}`} />
                        <span className="flex-1">{n}</span>
                        <span className="text-white/50">deploying…</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold tracking-widest uppercase shadow-[0_10px_20px_-10px_rgba(16,185,129,0.7)]">
              Live now
            </div>
          </div>
        </div>

        {/* BENTO GRID */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-4 md:auto-rows-[200px] gap-4">
          {BENTO.map((b, i) => {
            const Icon = b.icon;
            return (
              <article
                key={i}
                className={`relative rounded-2xl border border-white/10 overflow-hidden group hover:border-white/25 transition ${b.span || ''}`}
                style={{
                  boxShadow: '0 20px 50px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
                }}
              >
                {b.image ? (
                  <>
                    <img src={b.image} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    <div className={`absolute inset-0 mix-blend-multiply opacity-70 bg-gradient-to-br ${b.accent}`} />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/85" />
                  </>
                ) : (
                  <div className={`absolute inset-0 bg-gradient-to-br ${b.accent} opacity-90`} />
                )}
                <div className="relative h-full p-5 flex flex-col text-white">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-white/15 border border-white/25 flex items-center justify-center backdrop-blur">
                      <Icon size={14} />
                    </div>
                    <span className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/90">
                      {b.label}
                    </span>
                  </div>
                  <div className="mt-auto">
                    <h3 className="text-xl md:text-2xl font-black tracking-tight leading-tight">
                      {b.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] text-white/85 leading-relaxed">{b.body}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
